import type { Block, TemplateContent } from "@aswin.dev/types";
import { isSection } from "@aswin.dev/types";

/** Walk block tree — section columns included. */
function walkBlockSubtree(
  blocks: Block[] | undefined,
  predicate: (b: Block) => boolean,
): boolean {
  if (!blocks?.length) return false;
  for (const block of blocks) {
    if (predicate(block)) return true;
    if (isSection(block)) {
      for (const column of block.children) {
        if (!Array.isArray(column)) continue;
        if (walkBlockSubtree(column, predicate)) return true;
      }
    }
  }
  return false;
}

export function blockTreeContainsBlockId(
  blocks: Block[] | undefined,
  blockId: string,
): boolean {
  return walkBlockSubtree(blocks, (b) => b.id === blockId);
}

export function blockTreeContainsFormBlock(
  blocks: Block[] | undefined,
): boolean {
  return walkBlockSubtree(blocks, (b) => b.type === "form");
}

/** True if any canvas page / root blocks tree contains a form block. */
export function templateHasFormBlock(
  content: TemplateContent | null | undefined,
): boolean {
  if (!content) return false;
  const pages = content.canvasPages;
  if (pages?.length) {
    for (const page of pages) {
      if (blockTreeContainsFormBlock(page.blocks)) return true;
    }
    return false;
  }
  return blockTreeContainsFormBlock(content.blocks);
}

/**
 * Canvas page index (0-based) containing the block id, or `null` when
 * multi-page canvas is absent or block is not found in any page.
 */
export function indexOfCanvasPageContainingBlock(
  content: TemplateContent,
  blockId: string,
): number | null {
  const pages = content.canvasPages;
  if (!pages?.length) return null;
  for (let i = 0; i < pages.length; i++) {
    if (blockTreeContainsBlockId(pages[i].blocks, blockId)) return i;
  }
  return null;
}

/** True iff any strictly earlier canvas page contains a subscribe / form block. */
export function priorCanvasStepsHaveFormBlock(
  content: TemplateContent,
  discountStepIndex: number,
): boolean {
  const pages = content.canvasPages;
  if (!pages?.length || discountStepIndex <= 0) return false;
  const end = Math.min(discountStepIndex, pages.length);
  for (let i = 0; i < end; i++) {
    if (blockTreeContainsFormBlock(pages[i].blocks)) return true;
  }
  return false;
}

/**
 * When `canvasPages` is used, disallow `none` and `auto_code` until some prior
 * step has a form opt-in (`form` block). Manual Shopify code stays available.
 */
export function dynamicDiscountModesLockedForCouponBlock(
  content: TemplateContent,
  couponBlockId: string,
): boolean {
  const pages = content.canvasPages;
  if (!pages?.length) return false;
  const idx = indexOfCanvasPageContainingBlock(content, couponBlockId);
  if (idx === null) return false;
  if (idx <= 0) return true;
  return !priorCanvasStepsHaveFormBlock(content, idx);
}
